/**
 * Megaport Access Control Vue Plugin
 * @description Plugin to control user through through templates and view controls
 *
 * Aims:
 * - Directive to control rendering of elements
 * - Instance method for inline JS controls
 *
 * Usage:
 * Add directive to UI element: v-mp-access:[userGroups]="permissionsList"
 *
 * Where userGroups is the list of user permissions (via vuex/user/userGroups)
 * and permissionsList is a list of matching permissions
 */

/**
 * Returns a boolean value if the user is a member of the request group
 * @param {Array} userGroups List of groups the user has
 * @param {Array} requestedGroups List of groups that has permission
 * @returns {Boolean} True if the user is a member of the requested group
 */
function isGroupMember(userGroups, requestedGroups) {
  return requestedGroups.some(groupName => {
    return userGroups.includes(groupName.toLowerCase())
  })
}

const mpAccessDirective = {
  bind: (el, binding, vnode) => {
    // Escape if there are no values; assume user has access
    if (!binding.value || !binding.value.length) {
      return true
    }

    const userGroups = binding.arg
    let requestedGroups = binding.value
    if (!Array.isArray(requestedGroups)) {
      requestedGroups = requestedGroups
        .replace(/\'*/gm, '') // eslint-disable-line
        .trim()
        .split(',')
    }

    if (!userGroups || !requestedGroups) {
      throw new TypeError("You must provide both a list of groups the user has, and a list of groups to check against; in the format v-mp-access[userGroups]=\"'groupMember1','groupMember2'\"")
    }

    // if the requestGroups DO NOT match an item in the userGroups remove the element from the VDom
    const groupMember = isGroupMember(userGroups, requestedGroups)

    if (!groupMember) {
      vnode.elm.style.display = 'none'
    }
  },
}

/**
 * Instance method checks if user is member of requested group
 * @param {Array} userGroups List of groups the user has
 * @param {*} requestedGroups List of groups that has permission
 * @returns {Boolean} True if the user is a member of the requested group
 */
function mpAccessMethod(userGroups, requestedGroups) {
  if (!Array.isArray(userGroups) || !Array.isArray(requestedGroups)) {
    throw new TypeError('You must provide both a list of groups the user has, and a list of groups to check against. E.g $mpAccess(userGroupsList, groupMemberList)')
  }
  return isGroupMember(userGroups, requestedGroups)
}

/**
 * Vue plugin declaration
 */
const mpAccessControlPlugin = {
  install(Vue) {
    // Directive
    Vue.directive('mp-access', mpAccessDirective)

    // Instance method
    Vue.prototype.$mpAccess = mpAccessMethod
  },
}

export default mpAccessControlPlugin
